import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { ShieldAlert, AlertTriangle, ChevronRight } from "lucide-react";
import { getBullyingMonitor } from "../lib/api";

/** Admin-only dashboard panel summarizing active bullying warnings + open investigations. */
export default function BullyingMonitorCard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    getBullyingMonitor().then((r) => setData(r.data)).catch(() => setData({ warnings: [], investigations: [] }));
  }, []);

  if (!data) return null;
  const warnings = (data.warnings || []).filter((w) => !w.dismissed);
  const investigations = data.investigations || [];
  if (warnings.length === 0 && investigations.length === 0) return null;

  const go = () => navigate("/reports?tab=bullying-monitor");

  return (
    <Card className="border-red-200" data-testid="dashboard-bullying-monitor">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-red-600" /> Bullying &amp; Harassment Monitor
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex gap-3">
          {investigations.length > 0 && <Badge className="bg-red-100 text-red-800">{investigations.length} open investigation(s)</Badge>}
          {warnings.length > 0 && <Badge className="bg-amber-100 text-amber-800">{warnings.length} active warning(s)</Badge>}
        </div>
        <div className="space-y-1.5">
          {investigations.slice(0, 3).map((inv) => (
            <button key={inv.id} onClick={go} className="w-full text-left flex items-center justify-between gap-2 p-2 rounded border border-red-200 bg-red-50/60 hover:bg-red-50" data-testid={`dashboard-investigation-${inv.id}`}>
              <span className="text-sm truncate"><ShieldAlert className="w-3.5 h-3.5 inline mr-1 text-red-600" /><span className="font-medium">{inv.victim_name}</span> ← {inv.aggressor_name}</span>
              <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
            </button>
          ))}
          {warnings.slice(0, 3).map((w) => (
            <button key={w.pair_key} onClick={go} className="w-full text-left flex items-center justify-between gap-2 p-2 rounded border border-amber-200 bg-amber-50/60 hover:bg-amber-50" data-testid={`dashboard-warning-${w.pair_key}`}>
              <span className="text-sm truncate"><AlertTriangle className="w-3.5 h-3.5 inline mr-1 text-amber-600" />
                {w.kind === "multi_aggressor"
                  ? <><span className="font-medium">{w.victim_name}</span> — {w.aggressor_count} aggressors</>
                  : <><span className="font-medium">{w.victim_name}</span> ← {w.aggressor_name} ({w.count})</>}
              </span>
              <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
            </button>
          ))}
        </div>
        <button onClick={go} className="text-xs text-[#00305E] hover:underline" data-testid="dashboard-bullying-open">Open the Bullying &amp; Harassment Monitor →</button>
      </CardContent>
    </Card>
  );
}
